import * as React from "react"

import {PricingOption, PaymentType, ProcessDirection, CollationType, State, Status} from "./app";
import {Group, Item} from "../../../lib/sbhtml/field";
import {Form} from "../../../lib/sbhtml/form";
import {Input} from "../../../lib/sbhtml/input";
import {Checkbox} from "../../../lib/sbhtml/checkbox";
import {SingleBox as SingleSelect, FlexiBox as FlexiSelect} from "../../../lib/sbhtml/select";
import {Notification} from "../../../lib/sbhtml/notification";

type ProcessFormProps = ValueProps & CallbackProps

export type ValueProps = State & PrivateValueProps & PublicValueProps

type PrivateValueProps = {
	collationEnabled: boolean
	hasPaymentField: boolean
}

export type PublicValueProps = {
	pricingOptions: PricingOption[]
	disabledPricingOptions: string[]
	statuses: Status[]
	paymentTypes: PaymentType[]
}

export type CallbackProps = {
	setName: (name: string) => void
	setMultiPricingOption: (multi: boolean) => void
	setPricingOptions: (types: string[]) => void
	setMultiStatus: (multi: boolean) => void
	setStatuses: (statuses: string[]) => void
	setPaymentType: (pt: PaymentType) => void
	setProcessDays: (processDays: string) => void
	setProcessDirection: (processDirection: ProcessDirection) => void
	setCollationType: (ct: CollationType) => void
	setSendEmails: (sendEmails: boolean) => void
	setEnabled: (enabled: boolean) => void
	saveProcess: () => void
	saveProcessThenReturn: () => void
	onCancel: () => void
}

export const ProcessForm = (props: ProcessFormProps) => {
	return <Form
		hasSave={true}
		onSubmit={props.saveProcess}
		hasCancel={true}
		onCancel={props.onCancel}
		isSubmitting={props.submitting}
		onSaveReturn={props.saveProcessThenReturn}
	>
		<Group>
			<Item label="Name">
				<Input name={"name"} value={props.name} onChange={props.setName} />
			</Item>
			<Item label="Membership Type(s)">
				<PricingOptions {...props}/>
				<Checkbox label={"Multiple"} checked={props.multiPricingOption} value='1' onChange={props.setMultiPricingOption} />
				{props.disabledPricingOptions.length > 0 && <div style={{maxWidth: "500px"}}>
					<Notification type={"info"}>
						The following Membership Type(s) cannot be auto renewed because the renewal price is determined from a form field:
						The following Membership Type(s) cannot be auto renewed because the renewal price is determined from a form field:
						<ul>
							{props.disabledPricingOptions.map((id) => <li key={id}>{typeLabel(id, props.pricingOptions)}</li>)}
						</ul>
					</Notification>
				</div>}
			</Item>
			<Item label="Membership Statuses">
				<Statuses {...props}/>
				<Checkbox label={"Multiple"} checked={props.multiStatus} value='1' onChange={props.setMultiStatus} />
			</Item>
			{props.hasPaymentField && <Item label="Only renew members with payment type">
				<SingleSelect name={"paymentType"} value={props.selectedPaymentType} onChange={p => props.setPaymentType(isPaymentType(p) ? p : 'all')}>
					{props.paymentTypes.map((pt) => <option key={pt} value={pt}>{paymentLabel(pt)}</option>)}
				</SingleSelect>
			</Item>}
			<Item label="Process Days">
				<Input
					name={"days"}
					value={props.processDays}
					onChange={props.setProcessDays}
					numeric={{min: 0}}
				/>
				<SingleSelect name={"beforeAfter"} value={props.processDirection} onChange={(processDirection) => {
					props.setProcessDirection(isProcessDirection(processDirection) ? processDirection : 'before')}
				}>
					<option key={'before'} value={'before'}>before expiry date</option>
					<option key={'after'} value={'after'}>after expiry date</option>
				</SingleSelect>
			</Item>
			<Item label="Collation By">
				<SingleSelect name={"collation"} value={props.collationType} enabled={props.collationEnabled} onChange={(collationType) => {
					props.setCollationType(isCollationType(collationType) ? collationType : 'none')}
				}>
					<option key={'none'} value={'none'}>Do not collate</option>
					<option key={'member'} value={'member'}>Individual Memberships</option>
					<option key={'organisation'} value={'organisation'}>Organisation Memberships</option>
				</SingleSelect>
			</Item>
			<Item label="Send Emails">
				<Checkbox checked={props.sendEmails} value='1' onChange={props.setSendEmails} />
			</Item>
			<Item label="Enable Process">
				<Checkbox checked={props.enabled} value='1' onChange={props.setEnabled} />
			</Item>
		</Group>
	</Form>
}

const PricingOptions = (props: ProcessFormProps) => {
	return <FlexiSelect
		name="pricingOptions"
		multiple={props.multiPricingOption}
		value={props.selectedPricingOptions}
		onChange={props.setPricingOptions}
	>
		{props.pricingOptions.map((po) => <option
				key={po.id}
				value={po.id}
				disabled={isDisabledType(po, props.disabledPricingOptions)}
			>{po.label}</option>
		)}
	</FlexiSelect>
}

const Statuses = (props: ProcessFormProps) => {
	return <FlexiSelect
		name="statuses"
		multiple={props.multiStatus}
		value={props.selectedStatuses}
		onChange={props.setStatuses}
		>
		{props.statuses.map((s) => <option
				key={s.id}
				value={s.id}
				style={{backgroundColor: s.color}}
			>{s.label}</option>)}
	</FlexiSelect>
}

const isDisabledType = (type: PricingOption, disabledTypes: string[]): boolean => disabledTypes.find((id) => id == type.id) != undefined
const isProcessDirection = (value:string): value is ProcessDirection => value == "before" || value == "after"
const isPaymentType = (value:string): value is PaymentType => value == "all" || value == "scheduled payments" || value == "reusable payer"
const isCollationType = (value:string): value is CollationType => value == "none" || value == "member" || value == "organisation"

const typeLabel = (id: string, types: PricingOption[]): string => {
	const type = types.find((t) => t.id == id)
	return type ? type.label : ''
}

const paymentLabel = (pt: PaymentType): string => {
	switch(pt) {
		case "all":
			return "All";
		case "scheduled payments":
			return "Scheduled Payments";
		case "reusable payer":
			return "Automatic Payments";
	}
}



// WEBPACK FOOTER //
// ./src/membership3/auto-renewal/process-form/presentation.tsx